import { Button, DividerItem, Flex, Text } from 'components/ui';
import { ListingRead } from 'lib/api/generated';
import 'react-responsive-carousel/lib/styles/carousel.min.css'; // requires a loader
import { useMedia } from 'react-use';
import styled, { css } from 'styled-components';
import { breakpoints } from '../../styles/breakpoints';
import { globalTheme } from '../../styles/global-theme';

const SellerContactContainer = styled.div<{ isMobile: boolean }>`
  background-color: ${globalTheme.colors.background};
  padding: 60px;
  border-radius: 16px;
  ${({ isMobile }) =>
    isMobile &&
    css`
      padding: 20px;
    `};
`;

const Divider = styled(DividerItem)`
  display: flex;
  justify-content: space-between;
`;

const TextContainer = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
`;

const ActionButtonContainer = styled(Flex)`
  margin: auto;
  max-width: 80%;
  margin-top: ${globalTheme.space[1]};
  .request-callback-btn {
    background-color: ${globalTheme.colors.pink};
    &:hover {
      background-color: ${globalTheme.colors.lightPink};
      color: black;
    }
  }
  .send-offer-btn {
    background-color: black;
    &:hover {
      background-color: ${globalTheme.colors.stockbookBorder};
      color: black;
    }
  }
`;

const CButton = styled(Button)`
  width: 100%;
  max-width: 300px;
`;

interface ListingsDetailProps {
  listingDetails: ListingRead;
  onCTABtnClick?: (type: 'enquiry' | 'offer' | 'callback') => void;
}

function SellerContactInfo({
  listingDetails,
  onCTABtnClick,
}: ListingsDetailProps) {
  const isMobile = useMedia(`(max-width: ${breakpoints.medium})`, false);

  return (
    <>
      <Text
        as='h3'
        fontSize={4}
        fontWeight='bold'
        style={{
          padding: globalTheme.space[2],
          paddingBottom: globalTheme.space[0],
        }}
      >
        Seller Contact Information
      </Text>
      <SellerContactContainer isMobile={isMobile}>
        {/* public listing */}
        {!listingDetails?.org?.name && (
          <Flex direction='column'>
            <Text fontWeight='bold' fontSize={2} textAlign='center'>
              This user has chosen to keep their contact details private.
            </Text>
            <Text fontSize={1} textAlign='center'>
              You can send them an enquiry using the buttons below, if they're
              interested, they’ll get in touch directly and you'll then be able
              to see who they are.
            </Text>
            {onCTABtnClick && (
              <ActionButtonContainer
                stackOnMedium
                alignItems='center'
                justifyContent='center'
                gap={0}
              >
                <CButton
                  size='small'
                  variant='outline'
                  onClick={() => onCTABtnClick?.('enquiry')}
                >
                  Ask a question
                </CButton>

                <CButton
                  size='small'
                  className='send-offer-btn'
                  onClick={() => onCTABtnClick?.('offer')}
                >
                  Send an Offer
                </CButton>

                <CButton
                  size='small'
                  className='request-callback-btn'
                  onClick={() => onCTABtnClick?.('callback')}
                >
                  Request Callback
                </CButton>
              </ActionButtonContainer>
            )}
          </Flex>
        )}

        {listingDetails?.org && (
          <>
            {listingDetails?.creator?.firstname &&
              listingDetails?.creator?.lastname && (
                <Divider>
                  <TextContainer>
                    <Text>Name:</Text>
                    <Text>
                      {listingDetails?.creator?.firstname}{' '}
                      {listingDetails?.creator?.lastname}
                    </Text>
                  </TextContainer>
                </Divider>
              )}

            {listingDetails?.org?.name && (
              <Divider>
                <TextContainer>
                  <Text>Organisation:</Text>
                  <Text>{listingDetails?.org?.name} </Text>
                </TextContainer>
              </Divider>
            )}
            {listingDetails?.creator?.phone && (
              <Divider>
                <TextContainer>
                  <Text>Phone:</Text>
                  <Text>{listingDetails?.creator?.phone} </Text>
                </TextContainer>
              </Divider>
            )}
            {listingDetails?.creator?.email && (
              <Divider>
                <TextContainer>
                  <Text>Email:</Text>
                  <Text>{listingDetails?.creator?.email} </Text>
                </TextContainer>
              </Divider>
            )}
            {listingDetails?.listing_address?.pic && (
              <Divider>
                <TextContainer>
                  <Text>PIC:</Text>
                  <Text>{listingDetails?.listing_address?.pic} </Text>
                </TextContainer>
              </Divider>
            )}
            {listingDetails?.listing_address?.address && (
              <Divider>
                <TextContainer>
                  <Text>Location:</Text>
                  <Text>
                    {listingDetails?.listing_address?.address},{' '}
                    {listingDetails?.listing_address?.suburb}{' '}
                    {listingDetails?.listing_address?.state}{' '}
                    {listingDetails?.listing_address?.postcode}
                  </Text>
                </TextContainer>
              </Divider>
            )}
          </>
        )}

        {/* Anonymous listing */}
        {!listingDetails?.org && (
          <>
            <Text fontSize={5} fontWeight='bold' textAlign='center'>
              Private listing!
            </Text>
            <Text fontSize={2} textAlign='center'>
              {' '}
              To contact the buyer, use the <strong>Ask a question</strong>{' '}
              button above and the seller will be sent an SMS with your details.
            </Text>
          </>
        )}
      </SellerContactContainer>
    </>
  );
}
export default SellerContactInfo;
